import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { useCallback, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react'

import { T, tt } from '@agos/shared'

import { cn } from '../cn'
import { EmptyState } from './feedback'

const PAGE_SIZE_OPTIONS = [20, 50, 100, -1] as const
const SHOW_ALL = -1

export interface Column<Row> {
  key: string
  header: string
  /**
   * Hover text on the header — §9.177.
   *
   * For a column whose heading cannot carry its own explanation. *Kontrola* is the case it was added
   * for: the owner asked what it was, which is the answer.
   */
  headerTitle?: string
  className?: string
  headerClassName?: string
  render: (row: Row, index: number) => ReactNode
  sortable?: boolean
  /** Extracted value for automatic sorting. */
  sortValue?: (row: Row) => string | number | null | undefined
  /** Custom comparison; takes precedence over `sortValue`. */
  sortFn?: (left: Row, right: Row) => number
}

type SortDirection = 'asc' | 'desc'
interface SortEntry {
  key: string
  direction: SortDirection
}

function SortIcon({
  active,
  direction,
  priority,
}: {
  active: boolean
  direction?: SortDirection
  priority?: number
}) {
  if (!active) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />
  return (
    <span className="ml-1 inline-flex items-center gap-0.5 text-primary">
      {direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {priority != null && priority > 0 && (
        <span className="text-[9px] font-bold leading-none">{priority + 1}</span>
      )}
    </span>
  )
}

function PaginationBar({
  page,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  rawPageSize,
  onPageSizeChange,
}: {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  pageSize: number
  rawPageSize: number
  onPageSizeChange?: (size: number) => void
}) {
  if (totalPages <= 1 && !onPageSizeChange) return null

  const firstOnPage = totalItems === 0 ? 0 : (page - 1) * pageSize + 1
  const lastOnPage = Math.min(page * pageSize, totalItems)

  const jumpToTypedPage = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    const typed = Number.parseInt((event.target as HTMLInputElement).value, 10)
    if (typed >= 1 && typed <= totalPages) onPageChange(typed)
  }

  return (
    <div className="flex items-center justify-between pt-3 border-t border-border mt-1 gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">
          {firstOnPage}–{lastOnPage} z {totalItems}
        </span>
        {onPageSizeChange && (
          <select
            value={rawPageSize}
            aria-label={tt('Počet na stranu')}
            onChange={(event) => onPageSizeChange(Number.parseInt(event.target.value, 10))}
            className="text-xs px-2 py-1 rounded-lg bg-background border border-border outline-none focus:ring-2 focus:ring-primary/20"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size === SHOW_ALL ? tt('Všetko') : `${size} / strana`}
              </option>
            ))}
          </select>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            aria-label={tt('Prvá strana')}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-all"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label={tt('Predchádzajúca strana')}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <input
            key={page}
            type="number"
            min={1}
            max={totalPages}
            defaultValue={page}
            aria-label={tt('Strana')}
            onKeyDown={jumpToTypedPage}
            onBlur={(event) => {
              const typed = Number.parseInt(event.target.value, 10)
              if (typed >= 1 && typed <= totalPages && typed !== page) onPageChange(typed)
            }}
            className="w-12 text-center text-xs font-bold py-1 rounded-lg bg-background border border-border outline-none focus:ring-2 focus:ring-primary/20"
          />
          <span className="text-xs text-muted-foreground">/ {totalPages}</span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            aria-label={tt('Ďalšia strana')}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-all"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={page >= totalPages}
            aria-label={tt('Posledná strana')}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-all"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}

/** Slovak collation, so Č sorts after C rather than after Z. */
function compareRows<Row>(column: Column<Row>, left: Row, right: Row): number {
  if (column.sortFn) return column.sortFn(left, right)
  if (!column.sortValue) return 0

  const leftValue = column.sortValue(left)
  const rightValue = column.sortValue(right)
  if (leftValue == null && rightValue == null) return 0
  if (leftValue == null) return 1
  if (rightValue == null) return -1
  if (typeof leftValue === 'number' && typeof rightValue === 'number') return leftValue - rightValue
  return String(leftValue).localeCompare(String(rightValue), 'sk')
}

interface DataTableProps<Row> {
  columns: Column<Row>[]
  data: Row[]
  pageSize?: number
  emptyMessage?: string
  emptyIcon?: ReactNode
  rowKey: (row: Row, index: number) => string | number
  /**
   * Attributes for the row element — §9.201.
   *
   * A screen converting a hand-rolled table to this one usually has specs locating its rows by a
   * `data-*` hook, and losing that is the difference between a refactor and a rewrite. Applied to
   * the `<tr>` and to the card the narrow layout draws, so a selector works at either width.
   */
  rowAttributes?: (row: Row, index: number) => Record<string, string | number | undefined>
  onRowClick?: (row: Row) => void
  className?: string
  compact?: boolean
  stickyHeader?: boolean
  /** Supplying these three switches pagination to the server. */
  page?: number
  totalItems?: number
  onPageChange?: (page: number) => void
}

/**
 * The table every list page is built on, ported from legacy
 * `shared/data-table.tsx`.
 *
 * Two renderings of the same rows: a table on desktop and a definition list on
 * phones. That is not decoration — the boards run on tablets and phones in the
 * restaurant (§14.9), and a horizontally scrolling table is unusable there.
 *
 * Sorting is a chain: clicking a second column adds to it, clicking a sorted
 * column cycles ascending → descending → removed.
 */
export function DataTable<Row>({
  columns,
  data,
  pageSize: initialPageSize = 25,
  emptyMessage,
  emptyIcon,
  rowKey,
  rowAttributes,
  onRowClick,
  className,
  compact,
  stickyHeader,
  page: externalPage,
  totalItems: externalTotalItems,
  onPageChange: externalOnPageChange,
}: DataTableProps<Row>) {
  const [internalPage, setInternalPage] = useState(1)
  const [sortChain, setSortChain] = useState<SortEntry[]>([])
  const [internalPageSize, setInternalPageSize] = useState<number>(initialPageSize)

  const isServerPaginated = externalPage != null && externalOnPageChange != null
  const currentPage = isServerPaginated ? externalPage : internalPage
  const setPage = isServerPaginated ? externalOnPageChange : setInternalPage

  // FB-008: legacy resets the page from inside a `useMemo`, which is a state
  // update disguised as a computation. Same behaviour — filtering a list sends
  // you back to page one — expressed as React's sanctioned "adjust state when
  // the input changed" pattern, so no extra render is scheduled.
  const [rowCountAtLastReset, setRowCountAtLastReset] = useState(data.length)
  if (rowCountAtLastReset !== data.length) {
    setRowCountAtLastReset(data.length)
    if (!isServerPaginated) setInternalPage(1)
  }

  const toggleSort = (key: string) => {
    setSortChain((chain) => {
      const position = chain.findIndex((entry) => entry.key === key)
      if (position === -1) return [...chain, { key, direction: 'asc' }]
      if (chain[position]?.direction === 'asc') {
        return chain.map((entry, index) =>
          index === position ? { ...entry, direction: 'desc' } : entry,
        )
      }
      return chain.filter((_, index) => index !== position)
    })
    if (!isServerPaginated) setInternalPage(1)
  }

  const sortedRows = useMemo(() => {
    if (sortChain.length === 0) return data
    const columnsByKey = new Map(columns.map((column) => [column.key, column]))
    return [...data].sort((left, right) => {
      for (const { key, direction } of sortChain) {
        const column = columnsByKey.get(key)
        if (!column || (!column.sortFn && !column.sortValue)) continue
        const comparison = compareRows(column, left, right)
        if (comparison !== 0) return direction === 'asc' ? comparison : -comparison
      }
      return 0
    })
  }, [data, sortChain, columns])

  const rawPageSize = isServerPaginated ? initialPageSize : internalPageSize
  const showingAll = rawPageSize === SHOW_ALL
  const pageSize = showingAll ? sortedRows.length || 1 : rawPageSize

  const totalItems = isServerPaginated ? (externalTotalItems ?? data.length) : sortedRows.length
  const totalPages = showingAll ? 1 : Math.max(1, Math.ceil(totalItems / pageSize))
  const visibleRows = isServerPaginated
    ? data
    : showingAll
      ? sortedRows
      : sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const changePageSize = useCallback((size: number) => {
    setInternalPageSize(size)
    setInternalPage(1)
  }, [])

  if (data.length === 0 && !isServerPaginated) {
    return <EmptyState description={emptyMessage} icon={emptyIcon} />
  }

  const cellPadding = compact ? 'px-3 py-2' : 'px-4 py-3'
  const sortableColumns = columns.filter(
    (column) => column.sortable && (column.sortFn || column.sortValue),
  )

  return (
    <div className={cn('rounded-xl border border-border bg-card overflow-hidden', className)}>
      {sortableColumns.length > 0 && (
        <div className="md:hidden flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <T>Zoradiť:</T>
          </span>
          <select
            value={sortChain[0]?.key ?? ''}
            onChange={(event) => {
              const key = event.target.value
              if (!key) {
                setSortChain([])
                return
              }
              const existing = sortChain.find((entry) => entry.key === key)
              setSortChain([{ key, direction: existing?.direction ?? 'asc' }])
              if (!isServerPaginated) setInternalPage(1)
            }}
            className="text-xs px-2 py-1 rounded-lg bg-background border border-border outline-none focus:ring-2 focus:ring-primary/20 flex-1 min-w-0"
          >
            <option value="">-</option>
            {sortableColumns.map((column) => (
              <option key={column.key} value={column.key}>
                {column.header}
              </option>
            ))}
          </select>
          {sortChain.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const current = sortChain[0]
                if (!current) return
                setSortChain([
                  { key: current.key, direction: current.direction === 'asc' ? 'desc' : 'asc' },
                ])
              }}
              aria-label={tt('Zmeniť smer zoradenia')}
              className="p-1.5 rounded-lg bg-background border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              {sortChain[0]?.direction === 'asc' ? (
                <ArrowUp className="w-3.5 h-3.5" />
              ) : (
                <ArrowDown className="w-3.5 h-3.5" />
              )}
            </button>
          )}
        </div>
      )}

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className={cn('bg-muted/50', stickyHeader && 'sticky top-0 z-10')}>
            <tr>
              {columns.map((column) => {
                const isSortable = column.sortable && (column.sortFn || column.sortValue)
                const sortPosition = sortChain.findIndex((entry) => entry.key === column.key)
                const isSorted = sortPosition !== -1
                return (
                  <th
                    key={column.key}
                    title={column.headerTitle}
                    onClick={isSortable ? () => toggleSort(column.key) : undefined}
                    className={cn(
                      cellPadding,
                      'text-left text-xs font-bold text-muted-foreground uppercase tracking-wider whitespace-nowrap',
                      isSortable && 'cursor-pointer select-none hover:text-foreground transition-colors',
                      isSorted && 'text-foreground',
                      column.headerClassName,
                    )}
                  >
                    {column.header}
                    {isSortable && (
                      <SortIcon
                        active={isSorted}
                        direction={sortChain[sortPosition]?.direction}
                        priority={sortChain.length > 1 ? sortPosition : undefined}
                      />
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                {...rowAttributes?.(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-t border-border transition-colors',
                  onRowClick ? 'cursor-pointer hover:bg-muted/30' : 'hover:bg-muted/20',
                )}
              >
                {columns.map((column) => (
                  <td key={column.key} className={cn(cellPadding, 'text-foreground', column.className)}>
                    {column.render(row, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden divide-y divide-border">
        {visibleRows.map((row, index) => (
          <div
            key={rowKey(row, index)}
            {...rowAttributes?.(row, index)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn('p-3 transition-colors', onRowClick && 'cursor-pointer active:bg-muted/40')}
          >
            <dl className="space-y-1.5">
              {columns.map((column) => (
                <div key={column.key} className="flex items-start gap-3 min-w-0">
                  <dt
                    title={column.headerTitle}
                    className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0 pt-0.5 w-24 leading-tight"
                  >
                    {column.header}
                  </dt>
                  <dd className={cn('text-sm text-foreground min-w-0 flex-1 break-words', column.className)}>
                    {column.render(row, index)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      <div className="px-4 pb-3">
        <PaginationBar
          page={currentPage}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={totalItems}
          pageSize={pageSize}
          rawPageSize={rawPageSize}
          onPageSizeChange={isServerPaginated ? undefined : changePageSize}
        />
      </div>
    </div>
  )
}
